import { mapAttachmentApiModel } from '@webapp/core/data-access/mappers/mapAttachment'
import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { mapCustomFieldValue } from '@webapp/core/data-access/mappers/mapCustomField'
import { type UserConstructor } from '@webapp/platform/iam'
import { mapOrderItemAccount } from './mapOrderItemAccountApiModel'
import { mapOrderItemBlanketDetails } from './mapOrderItemBlanketDetails'
import { mapOrderItemCatalogItem } from './mapOrderItemCatalogItem'
import { mapOrderItemPunchOutSupplier } from './mapOrderItemPunchOutSupplier'
import { mapOrderItemRecurringDetails } from './mapOrderItemRecurringDetails'
import { mapPreferredVendorApiModel } from './mapPreferredVendorApiModel'
import { type PunchOutStatusEnum } from '../../entities/enums/PunchOutStatusEnum'
import { type OrderItemConstructorData } from '../../entities/OrderItem'
import { type OrderItemApiModel } from '../api-models/OrderItemApiModel'

const mapPunchOutConfirmationStatus = (punchoutConfirmationStatusData: {
  confirmation_status: Nullable<PunchOutStatusEnum>
  confirmation_status_description: Nullable<string>
}) => {
  if (
    punchoutConfirmationStatusData.confirmation_status === null ||
    punchoutConfirmationStatusData.confirmation_status_description === null
  )
    return null

  return {
    status: punchoutConfirmationStatusData.confirmation_status,
    description: punchoutConfirmationStatusData.confirmation_status_description,
  }
}

const mapPurchaseOrder = (purchaseOrderData: {
  purchase_order: Nullable<number>
  purchase_order_uuid: Nullable<string>
  purchase_order_number: Nullable<string>
  purchaser: Nullable<UserConstructor>
  po_note: string
}) => {
  if (
    purchaseOrderData.purchase_order === null ||
    purchaseOrderData.purchase_order_uuid === null
  )
    return null

  return {
    id: purchaseOrderData.purchase_order,
    uuid: purchaseOrderData.purchase_order_uuid,
    purchaseOrderNumber: purchaseOrderData.purchase_order_number,
    comment: purchaseOrderData.po_note,
    purchaser: purchaseOrderData.purchaser,
  }
}

export const mapOrderItemApiModel = (
  orderItem: OrderItemApiModel
): OrderItemConstructorData => ({
  id: orderItem.id,
  name: orderItem.name,
  type: orderItem.type,
  status: orderItem.status,
  dateSubmitted: orderItem.submitted_date,
  dateRequired: orderItem.date_required,
  dateModified: orderItem.last_modified,
  account: mapOrderItemAccount(orderItem.account),
  customFields: orderItem.custom_fields.map(mapCustomFieldValue),
  attachments: orderItem.item_attachments.map(mapAttachmentApiModel),
  sku: orderItem.sku,
  comment: orderItem.lineComment,
  vendorComment: orderItem.po_note,
  currency: mapCurrency(orderItem.currency),
  preferredVendor: mapPreferredVendorApiModel(orderItem.pref_vendor),
  price: orderItem.price,
  quantity: orderItem.quantity,
  unit: orderItem.unit,
  shippingAmount: orderItem.shipping_amount,
  taxAmount: orderItem.tax_amount,
  totalCost: orderItem.total_cost,
  approvedTotalCost: orderItem.approved_total_cost,
  vendorName: orderItem.vendor,
  receivingMethod: orderItem.receive_method,
  blanketItemDetails: mapOrderItemBlanketDetails(orderItem.blanket),
  recurringItemDetails: mapOrderItemRecurringDetails(orderItem.recurrence),
  catalogItem: mapOrderItemCatalogItem(orderItem.catalog_item),
  punchoutSupplier: mapOrderItemPunchOutSupplier(orderItem),
  punchoutConfirmationStatus: mapPunchOutConfirmationStatus(orderItem),
  order: {
    uuid: orderItem.order_uuid,
    id: orderItem.orderNum,
    orderNumber: orderItem.order_number ?? orderItem.orderNum,
  },
  purchaseOrder: mapPurchaseOrder(orderItem),
  receivedQuantity: orderItem.receivedPassQty,
  dateFullyReceived: orderItem.fully_received_actual_receive_date,
  lineNumber: parseInt(orderItem.num, 10),
})
